// Custom hook that drives the EntryAnimation phase timeline.
// Replaces a complex if/else chain with a lookup table — each phase has a
// pure handler that updates the surface state. Cuts cyclomatic complexity
// from ~20 down to ~3 for the tick function.

import { useEffect, useRef } from 'react';

export const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
export const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export const T = {
  walkStart: 200,
  walkEnd: 2400,
  weighing: 2400,
  growStart: 3000,
  growEnd: 5800,
  burst: 5900,
  modal: 6700,
  total: 7000,
};

const clamp01 = (x) => Math.max(0, Math.min(1, x));

// Each phase: { until: ms, run: (now, ctx) => void }
// `run` mutates the setters in `ctx`. Order matters — first matching phase wins.
const buildTimeline = (ctx) => [
  {
    until: T.walkEnd,
    run: (now) => {
      const p = clamp01((now - T.walkStart) / (T.walkEnd - T.walkStart));
      ctx.setTranslateX(-220 + 220 * easeOutCubic(p));
      ctx.setPhase('walking');
    },
  },
  {
    until: T.growStart,
    run: () => { ctx.setTranslateX(0); ctx.setPhase('weighing'); },
  },
  {
    until: T.growEnd,
    run: (now) => {
      const p = clamp01((now - T.growStart) / (T.growEnd - T.growStart));
      ctx.setGrowProgress(easeInOutCubic(p));
      ctx.setWeight(Math.round(60 + (185 - 60) * p));
      ctx.setPhase('growing');
    },
  },
  {
    until: T.burst,
    run: () => { ctx.setGrowProgress(1); ctx.setWeight(185); ctx.setPhase('preburst'); },
  },
  { until: T.modal, run: () => ctx.setPhase('bursting') },
  {
    until: T.total,
    run: () => {
      ctx.setPhase('modal');
      if (ctx.isCustomer()) { ctx.markPlayed(); ctx.onComplete?.(); ctx.stop(); return; }
      ctx.setShowModal(true);
    },
  },
];

export const useEntryTimeline = ({
  startedAt, setPhase, setTranslateX, setGrowProgress, setWeight, setShowModal,
  customerRef, onCompleteRef, markPlayed,
}) => {
  const frame = useRef(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    const ctx = {
      setPhase, setTranslateX, setGrowProgress, setWeight, setShowModal,
      isCustomer: () => !!customerRef.current,
      onComplete: () => onCompleteRef.current?.(),
      markPlayed,
      stop: () => { stoppedRef.current = true; },
    };
    const timeline = buildTimeline(ctx);

    const tick = () => {
      if (stoppedRef.current) return;
      const now = Date.now() - startedAt.current;
      const phase = timeline.find((p) => now < p.until);
      if (!phase) return; // beyond total — finish (modal stays up until user closes)
      phase.run(now);
      frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
    // All deps below are stable refs/setters — intentionally empty for a single rAF loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
